import React, { useEffect, useState } from "react";
import "./SaleTarget.css";
import Box from "../../../../components/box/Box";
import user from "./users.png";
import { getListOfTeams } from "../../../../service/getListOfTeams";
import ButtonComponent from "../../../../components/CssComponents/ButtonComponent/ButtonComponent";
import AutoAssignModal from "./AutoAssignModal/AutoAssignModal";
import UpdateTargetAmmount from "./UpdateCurrentTargetAmount/UpdateTargetAmount";
import SetTargetModal from "./SetTargetModal/SetTargetModal";

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  getDocs,
  where,
} from "firebase/firestore";
import { db } from "../../../../firebase-config";
import { notification } from "antd";

const SalesTarget = () => {
  const [isAutoAssignModal, setIsAutoAssignModal] = useState(false);
  const [teams, setTeams] = useState([]);
  const [amounts, setAmounts] = useState({});
  const [assignedUsers, setAssignedUsers] = useState({}); // userId -> docId
  const [assignedDates, setAssignedDates] = useState({}); // userId -> assignedAt
  const [targetId, setTargetId] = useState(null);
  const [clubTargetValue, setClubTargetValue] = useState(0);
  const [data, setData] = useState(null);
  const [isUpdateModal, setIsUpdateModal] = useState(false);
  const [isTargetModal, setIsTargetModal] = useState(false);

  useEffect(() => {
    const fetchTeams = async () => {
      const teamsData = await getListOfTeams();
      setTeams(teamsData);
    };
    fetchTeams();
  }, []);

  function getCurrentMonth() {
    return new Date().getMonth() + 1;
  }

  function getCurrentYear() {
    return new Date().getFullYear();
  }

  const fetchSalesData = async () => {
    try {
      const q = query(
        collection(db, "targets"),
        where("targetMonth", "==", getCurrentMonth()),
        where("targetYear", "==", getCurrentYear())
      );

      const querySnapshot = await getDocs(q);

      const data = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      if (data.length > 0) {
        setData(data[0]);
        const target = data[0];
        setClubTargetValue(target.targetNumber);
        setTargetId(target.id);
        fetchAssignedAmounts(target.id);
      }
    } catch (error) {
      console.error("Error fetching target data:", error);
    }
  };

  useEffect(() => {
    fetchSalesData();
  }, []);

  const fetchAssignedAmounts = async (targetId) => {
    if (!targetId) return;

    try {
      const targetAssignRef = collection(
        db,
        "targets",
        targetId,
        "targetassign"
      );
      const querySnapshot = await getDocs(targetAssignRef);

      const assignedAmounts = {};
      const assignedDocs = {};
      const assignedDates = {};

      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        assignedAmounts[data.userId] = data.amount;
        assignedDocs[data.userId] = docSnap.id;
        assignedDates[data.userId] = data.assignedAt?.toDate?.() || null;
      });

      setAmounts(assignedAmounts);
      setAssignedUsers(assignedDocs);
      setAssignedDates(assignedDates);
    } catch (error) {
      console.error("Error fetching assigned amounts:", error);
    }
  };

  // Assign / Edit handler
  const handleAssign = async (userId) => {
    if (!targetId || !amounts[userId]) return;

    const newAmount = Number(amounts[userId]);

    try {
      const assignedTotal = Object.keys(assignedUsers).reduce((sum, id) => {
        if (id !== userId) {
          const amt = Number(amounts[id]) || 0;
          return sum + amt;
        }
        return sum;
      }, 0);

      const remainingAmount = Number(clubTargetValue) - assignedTotal;

      if (newAmount > remainingAmount) {
        notification.warning({
          message: `Enter an amount less than or equal to remaining: ${remainingAmount}`,
          placement: "topRight",
          duration: 3,
        });
        return;
      }

      const targetAssignRef = collection(
        db,
        "targets",
        targetId,
        "targetassign"
      );

      const now = new Date();

      if (assignedUsers[userId]) {
        const docRef = doc(
          db,
          "targets",
          targetId,
          "targetassign",
          assignedUsers[userId]
        );
        await updateDoc(docRef, {
          amount: newAmount,
          assignedAt: now,
          updatedAt: now,
        });
        notification.success({
          message: "Amount Updated Successfully",
          placement: "topRight",
          duration: 3,
        });
      } else {
        const docRef = await addDoc(targetAssignRef, {
          userId,
          amount: newAmount,
          assignedAt: now,
        });
        setAssignedUsers((prev) => ({ ...prev, [userId]: docRef.id }));
        setAssignedDates((prev) => ({ ...prev, [userId]: now }));
      }

      fetchAssignedAmounts(targetId);
    } catch (error) {
      console.error("Error assigning/editing amount:", error);
    }
  };

  // Undo assignment
  const handleUndo = async (userId) => {
    if (!targetId || !assignedUsers[userId]) return;

    try {
      const docRef = doc(
        db,
        "targets",
        targetId,
        "targetassign",
        assignedUsers[userId]
      );
      await deleteDoc(docRef);

      setAssignedUsers((prev) => {
        const copy = { ...prev };
        delete copy[userId];
        return copy;
      });
      setAmounts((prev) => {
        const copy = { ...prev };
        copy[userId] = "";
        return copy;
      });
      setAssignedDates((prev) => {
        const copy = { ...prev };
        delete copy[userId];
        return copy;
      });

      fetchAssignedAmounts(targetId);
    } catch (error) {
      console.error("Error undoing assignment:", error);
    }
  };

  // Auto-assign logic
  function assignTargets(totalTarget, existingAssignments) {
    const alreadyAssigned = existingAssignments.reduce(
      (a, b) => a + (b || 0),
      0
    );
    let remaining = totalTarget - alreadyAssigned;
    if (remaining < 0) throw new Error("Assigned amount exceeds total target!");

    const unassignedCount = existingAssignments.filter(
      (v) => !v || v === 0
    ).length;
    if (unassignedCount === 0) return existingAssignments;

    const base = Math.floor(remaining / unassignedCount);
    const remainder = remaining % unassignedCount;

    let result = [...existingAssignments];
    let distributed = 0;

    for (let i = 0; i < result.length; i++) {
      if (!result[i] || result[i] === 0) {
        result[i] = base + (distributed < remainder ? 1 : 0);
        distributed++;
      }
    }

    return result;
  }

  const autoAssignTargets = async () => {
    if (!targetId || !teams.length) return;

    try {
      const existingAssignments = teams.map((team) => {
        const val = amounts[team.id];
        return val ? Number(val) : 0;
      });

      const allAssigned = existingAssignments.every((val) => val > 0);
      if (allAssigned) {
        notification.success({
          message: "Targets are already assigned for all teams.",
          placement: "topRight",
          duration: 3,
        });

        return;
      }

      const autoAssigned = assignTargets(clubTargetValue, existingAssignments);
      const now = new Date();

      for (let i = 0; i < teams.length; i++) {
        const teamId = teams[i].id;
        const prevAssigned = existingAssignments[i]; // old value
        const assignedAmount = autoAssigned[i];

        if (prevAssigned > 0) continue;

        const targetAssignRef = collection(
          db,
          "targets",
          targetId,
          "targetassign"
        );

        if (assignedUsers[teamId]) {
          const docRef = doc(
            db,
            "targets",
            targetId,
            "targetassign",
            assignedUsers[teamId]
          );
          await updateDoc(docRef, {
            amount: assignedAmount,
            assignedAt: now,
            updatedAt: now,
          });
        } else {
          const docRef = await addDoc(targetAssignRef, {
            userId: teamId,
            amount: assignedAmount,
            assignedAt: now,
          });
          setAssignedUsers((prev) => ({ ...prev, [teamId]: docRef.id }));
          setAssignedDates((prev) => ({ ...prev, [teamId]: now }));
        }
      }
      fetchSalesData();
    } catch (error) {
      console.error("Error in auto assigning targets:", error);
    }
  };

  const [stats, setStats] = useState({
    totalUsers: 0,
    totalUsersGrowth: 0,
    totalTeamLeaders: 0,
    teamLeaderGrowth: 0,
    totalTeamMembers: 0,
    teamMemberGrowth: 0,
  });
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const snapshot = await getDocs(collection(db, "users"));
        if (snapshot.empty) return;

        const users = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // start of today
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);

        // yesterday
        const startOfYesterday = new Date(startOfToday);
        startOfYesterday.setDate(startOfYesterday.getDate() - 1);

        // --- Helpers ---
        const parseDate = (createdAt) => {
          if (!createdAt) return null;
          const [day, month, year] = createdAt.split(" ")[0].split("-");
          return new Date(`20${year}-${month}-${day}`);
        };

        // --- Total Users ---
        const totalUsers = users.length;

        const totalUsersYesterday = users.filter((u) => {
          const createdDate = parseDate(u.createdAt);
          return createdDate && createdDate < startOfToday;
        }).length;

        const totalUsersGrowth = totalUsersYesterday
          ? ((totalUsers - totalUsersYesterday) / totalUsersYesterday) * 100
          : 0;

        // --- Team Leader / Team Member Counts ---
        const teamLeaders = users.filter((u) => u.userTypes === "team_leader");
        const teamMembers = users.filter((u) => u.userTypes === "team_member");

        const totalTeamLeaders = teamLeaders.length;
        const totalTeamMembers = teamMembers.length;

        // Yesterday counts
        const teamLeadersYesterday = teamLeaders.filter((u) => {
          const createdDate = parseDate(u.createdAt);
          return createdDate && createdDate < startOfToday;
        }).length;

        const teamMembersYesterday = teamMembers.filter((u) => {
          const createdDate = parseDate(u.createdAt);
          return createdDate && createdDate < startOfToday;
        }).length;

        // Growth %
        const teamLeaderGrowth = teamLeadersYesterday
          ? ((totalTeamLeaders - teamLeadersYesterday) / teamLeadersYesterday) *
            100
          : 0;

        const teamMemberGrowth = teamMembersYesterday
          ? ((totalTeamMembers - teamMembersYesterday) / teamMembersYesterday) *
            100
          : 0;

        // --- Final Stats ---
        setStats({
          totalUsers,
          totalUsersGrowth: totalUsersGrowth.toFixed(1),
          totalTeamLeaders,
          teamLeaderGrowth: teamLeaderGrowth.toFixed(1),
          totalTeamMembers,
          teamMemberGrowth: teamMemberGrowth.toFixed(1),
        });
      } catch (e) {
        console.error("Error fetching user stats:", e);
      }
    };

    fetchStats();
  }, []);

  const totalAssignedAmount = Object.values(amounts).reduce(
    (sum, val) => sum + Number(val || 0),
    0
  );
  const remainingTarget = clubTargetValue - totalAssignedAmount;
  return (
    <div className="saletarget">
      <h1 className="heading">Sale & Targets</h1>
      <div className="flex justify-center items-center">
        <Box
          subhed={"Team Leaders"}
          value={stats.totalTeamLeaders}
          growth={stats.teamLeaderGrowth}
          logo={user}
        />
        <Box subhed={"User"} value={"40,689"} logo={user} />
        <Box subhed={"User"} value={"40,689"} logo={user} />
        <Box subhed={"User"} value={"40,689"} logo={user} />
      </div>
      <div className="salecontent">
        <div className=" mx-auto pt-6 pb-6 rounded-2xl shadow-lg flex items-center justify-between gap-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <h1 className="bigheading">
              Club Decided Target:{" "}
              <span className="text-green-600">₹{clubTargetValue}</span>
              <p className="text-2xl text-white">
                Remaining Target:{" "}
                <span className="text-red-500 font-semibold">
                  ₹{remainingTarget}
                </span>
              </p>
            </h1>
          </div>
          <div className="flex flex-wrap gap-3">
            <ButtonComponent
              buttonText={"Auto Assign"}
              onClick={() => autoAssignTargets()}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg transition-all"
            />
            <ButtonComponent
              buttonText={"Set Target"}
              onClick={() => setIsTargetModal(true)}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2 rounded-lg transition-all"
            />
            <ButtonComponent
              buttonText={"Edit Target"}
              onClick={() => setIsUpdateModal(true)}
              className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-4 py-2 rounded-lg transition-all"
            />
          </div>
        </div>

        <h1 className="heading">Team List</h1>

        <table className="table" cellSpacing={0}>
          <thead className="tablehead">
            <tr>
              <th>ID</th>
              <th>Team Name</th>
              <th>Amount</th>
              <th>Assigned Date</th>
              <th>Assign / Undo</th>
            </tr>
          </thead>
          <tbody className="tablebody">
            {teams.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center">
                  No data found
                </td>
              </tr>
            ) : (
              teams.map((item) => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td>{item.firstName + " " + item.lastName}</td>
                  <td>
                    <input
                      type="number"
                      placeholder="Enter Amount"
                      className="inputinstall"
                      value={amounts[item.id] || ""}
                      min={0}
                      max={remainingTarget}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (
                          value === "" ||
                          (Number(value) >= 0 && !isNaN(Number(value)))
                        ) {
                          setAmounts((prev) => ({ ...prev, [item.id]: value }));
                        }
                      }}
                    />
                  </td>
                  <td>
                    {assignedDates[item.id]
                      ? assignedDates[item.id].toLocaleDateString() +
                        " " +
                        assignedDates[item.id].toLocaleTimeString()
                      : "-"}
                  </td>
                  <td>
                    {assignedUsers[item.id] ? (
                      <>
                        <button
                          className="bg-red-500 text-white px-3 py-1 rounded mr-2"
                          onClick={() => handleUndo(item.id)}
                        >
                          Undo
                        </button>
                        <button
                          className="bg-blue-500 text-white px-3 py-1 rounded"
                          onClick={() => handleAssign(item.id)}
                        >
                          Update
                        </button>
                      </>
                    ) : (
                      <button
                        className="bg-green-500 text-white px-3 py-1 rounded"
                        onClick={() => handleAssign(item.id)}
                      >
                        Assign
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isAutoAssignModal && (
        <AutoAssignModal
          isAutoAssignModal={isAutoAssignModal}
          setIsAutoAssignModal={setIsAutoAssignModal}
        />
      )}
      {isTargetModal && (
        <SetTargetModal
          fetchSalesData={fetchSalesData}
          isTargetModal={isTargetModal}
          setIsTargetModal={setIsTargetModal}
        />
      )}
      {isUpdateModal && (
        <UpdateTargetAmmount
          data={data}
          fetchSalesData={fetchSalesData}
          isUpdateModal={isUpdateModal}
          setIsUpdateModal={setIsUpdateModal}
        />
      )}
    </div>
  );
};

export default SalesTarget;
